import { baseApi } from './baseApi';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
}

export interface TelegramLoginRequest {
  id: number;
  first_name: string;
  last_name?: string | null;
  username?: string | null;
  photo_url?: string | null;
  auth_date: number;
  hash: string;
}

export interface AuthResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      email?: string;
      telegramId?: number;
      firstName: string;
      lastName?: string;
      username?: string;
      authMethod: 'email' | 'telegram';
      language: string;
      isAdmin: boolean;
      isBanned: boolean;
      balance: number;
      referralBalance: number;
      cashbackBalance: number;
      createdAt: string;
      updatedAt: string;
    };
    token: string;
  } | {
    requiresOTP: true;
    tempUserId: string;
    email: string;
  };
}

export interface ErrorResponse {
  status: 'fail' | 'error';
  message: string;
}

export interface RegisterResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      email: string;
      firstName: string;
      authMethod: 'email';
      language: string;
      isAdmin: boolean;
      isBanned: boolean;
      balance: number;
      referralBalance: number;
      cashbackBalance: number;
      createdAt: string;
      updatedAt: string;
    };
  };
}

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      email: string;
      emailVerified: boolean;
      firstName: string;
      authMethod: 'email';
    };
  };
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  status: 'success';
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  status: 'success';
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ResetPasswordResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      email: string;
      firstName: string;
    };
  };
}

// Telegram Login with Code (Advanced)
export interface RequestTelegramLoginCodeRequest {
  telegramId: number;
}

export interface RequestTelegramLoginCodeResponse {
  status: 'success';
  message: string;
}

export interface VerifyTelegramLoginCodeRequest {
  telegramId: number;
  code: string;
}

export interface VerifyTelegramLoginCodeResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      telegramId: number;
      firstName: string;
      lastName?: string;
      username?: string;
      authMethod: 'telegram';
      balance: number;
      language: string;
      email?: string;
    };
    token: string;
  };
}

export interface AddCredentialsToTelegramAccountRequest {
  telegramId: number;
  email: string;
  password: string;
}

export interface AddCredentialsToTelegramAccountResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      _id: string;
      telegramId: number;
      email: string;
      firstName: string;
      authMethod: 'telegram';
    };
  };
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Login with Email
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User'],
    }),

    // Register with Email
    register: builder.mutation<RegisterResponse, RegisterRequest>({
      query: (userData) => ({
        url: '/auth/register',
        method: 'POST',
        body: userData,
      }),
    }),

    // Login with Telegram
    telegramLogin: builder.mutation<AuthResponse, TelegramLoginRequest>({
      query: (telegramData) => ({
        url: '/auth/telegram-login',
        method: 'POST',
        body: telegramData,
      }),
      invalidatesTags: ['User'],
    }),

    // Verify Email
    verifyEmail: builder.mutation<VerifyEmailResponse, VerifyEmailRequest>({
      query: ({ token }) => ({
        url: '/auth/verify-email',
        method: 'GET',
        params: { token },
      }),
    }),

    // Verify Email Code (6-digit code)
    verifyEmailCode: builder.mutation<VerifyEmailResponse, { email: string; code: string }>({
      query: ({ email, code }) => ({
        url: '/auth/verify-email-code',
        method: 'POST',
        body: { email, code },
      }),
    }),

    // Resend Verification Email
    resendVerification: builder.mutation<ResendVerificationResponse, ResendVerificationRequest>({
      query: (data) => ({
        url: '/auth/resend-verification',
        method: 'POST',
        body: data,
      }),
    }),

    // Forgot Password
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),

    // Verify Password Reset Code
    verifyResetCode: builder.mutation<{ status: string; message: string; data: { verifiedToken: string } }, { email: string; code: string }>({
      query: (data) => ({
        url: '/auth/verify-reset-code',
        method: 'POST',
        body: data,
      }),
    }),

    // Reset Password
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: data,
      }),
    }),

    // Request Telegram Login Code
    requestTelegramLoginCode: builder.mutation<
      RequestTelegramLoginCodeResponse,
      RequestTelegramLoginCodeRequest
    >({
      query: (data) => ({
        url: '/auth/telegram-login-request',
        method: 'POST',
        body: data,
      }),
    }),

    // Verify Telegram Login Code
    verifyTelegramLoginCode: builder.mutation<
      VerifyTelegramLoginCodeResponse,
      VerifyTelegramLoginCodeRequest
    >({
      query: (data) => ({
        url: '/auth/telegram-login-verify',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Add Credentials to Telegram Account
    addCredentialsToTelegramAccount: builder.mutation<
      AddCredentialsToTelegramAccountResponse,
      AddCredentialsToTelegramAccountRequest
    >({
      query: (data) => ({
        url: '/auth/telegram-login-add-credentials',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Logout (clears JWT cookie)
    logout: builder.mutation<{ status: string; message: string }, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),

    // Verify Login OTP (2FA step)
    verifyLoginOtp: builder.mutation<AuthResponse, { tempUserId: string; otp: string }>({
      query: (data) => ({
        url: '/auth/verify-login-otp',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Resend Login OTP
    resendLoginOtp: builder.mutation<{ status: string; message: string }, { tempUserId: string }>({
      query: (data) => ({
        url: '/auth/resend-login-otp',
        method: 'POST',
        body: data,
      }),
    }),

    // Toggle Email 2FA
    toggle2fa: builder.mutation<
      { status: string; message: string; data: { action: string; email?: string } },
      { enable: boolean; otp?: string }
    >({
      query: (data) => ({
        url: '/auth/toggle-2fa',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Confirm Enable 2FA
    confirmEnable2fa: builder.mutation<
      { status: string; message: string; data: { action: string } },
      { otp: string }
    >({
      query: (data) => ({
        url: '/auth/confirm-enable-2fa',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useTelegramLoginMutation,
  useVerifyEmailMutation,
  useVerifyEmailCodeMutation,
  useResendVerificationMutation,
  useForgotPasswordMutation,
  useVerifyResetCodeMutation,
  useResetPasswordMutation,
  useRequestTelegramLoginCodeMutation,
  useVerifyTelegramLoginCodeMutation,
  useAddCredentialsToTelegramAccountMutation,
  useLogoutMutation,
  useVerifyLoginOtpMutation,
  useResendLoginOtpMutation,
  useToggle2faMutation,
  useConfirmEnable2faMutation,
} = authApi;

